import { useState, useEffect, useCallback } from 'react';
import type { question } from '../../types.api';

const getLetter = (index: number) => String.fromCharCode(65 + index);

export const useQuestionEditor = (initialQuestion: question | null) => {
    const [formData, setFormData] = useState<any>(null);

    useEffect(() => {
        if (initialQuestion) {
            setFormData({
                ...initialQuestion,
                content: {
                    ...initialQuestion.content,
                    options: (initialQuestion.content.options ||[]).map((opt: any, index: number) => ({
                        ...opt,
                        key: opt.key || getLetter(index),
                    })),
                },
            });
        }
    },[initialQuestion]);

    const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFormData((prev: any) =>
            prev ? { ...prev, content: { ...prev.content, text: e.target.value } } : null
        );
    },[]);

    const handleOptionChange = useCallback((index: number, value: string) => {
        setFormData((prev: any) => {
            if (!prev) return null;
            const newOptions = [...(prev.content.options || [])];
            newOptions[index] = { ...newOptions[index], text: value };
            return { ...prev, content: { ...prev.content, options: newOptions } };
        });
    },[]);

    const handleAddOption = useCallback(() => {
        setFormData((prev: any) => {
            if (!prev) return null;
            const currentOptions = [...(prev.content.options || [])];
            const updatedOptions = [
                ...currentOptions,
                { key: '', text: '' },
            ].map((opt, index) => ({
                ...opt,
                key: getLetter(index),
            }));
            return { ...prev, content: { ...prev.content, options: updatedOptions } };
        });
    },[]);

    const handleRemoveOption = useCallback((index: number) => {
        setFormData((prev: any) => {
            if (!prev) return null;
            const filtered = (prev.content.options ||[]).filter((_: any, i: number) => i !== index);
            const remapped = filtered.map((opt: any, idx: number) => ({
                ...opt,
                key: getLetter(idx),
            }));
            return { ...prev, content: { ...prev.content, options: remapped } };
        });
    },[]);

    const handleFileSelected = useCallback((file: File | null) => {
        if (!file) return;
        setFormData((prev: any) => {
            if (!prev) return null;
            const localUrl = URL.createObjectURL(file);
            return {
                ...prev,
                content: { ...prev.content, image_url: localUrl },
                imageFile: file,
                removeImageFlag: false,
            };
        });
    },[]);

    const handleRemoveImage = useCallback(() => {
        setFormData((prev: any) => {
            if (!prev) return null;
            return {
                ...prev,
                content: { ...prev.content, image_url: undefined },
                imageFile: undefined,
                removeImageFlag: true,
            };
        });
    },[]);

    return {
        formData,
        handleTextChange,
        handleOptionChange,
        handleAddOption,
        handleRemoveOption,
        handleFileSelected,
        handleRemoveImage,
    };
};